import React, { useCallback, useMemo } from 'react';
import Select, { components } from 'react-select';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import { normalizeDefaultToNull } from '@utils/normalize/normalizeDefaultToNull';
import { normalizeStringToBooleanValue } from '@utils/normalize/normalizeStringToBooleanValue';

const ReactSelectOption = props => {
    const { data } = props;
    const isGroupHeader = data?.isGroupHeader === true;

    const nextProps = {
        ...props,
        innerProps: {
            ...props.innerProps,
            'data-testid': `rs-option-${props.value}`,
        },
        className: classNames(props.className, {
            'gi-rs__option--group-header': isGroupHeader,
        }),
    };

    return <components.Option {...nextProps} />;
};

const ReactSelect = ({
    inputActions,
    inputData: {
        disabled,
        id,
        inputClass,
        isInputRequired,
        handleAsNumbers,
        name,
        options = [],
        returnAsBoolean,
        type,
        value,
        isInModal,
    },
    label: { labelClass, text, ariaLabel },
    labelIcon = {},
    shouldParse = false,
    dropdownContainerClassName,
}) => {
    const intlApi = useIntl();

    const getOptionTextParsed = useCallback(
        optionText => {
            return shouldParse ? intlApi.formatMessage({ id: optionText }) : optionText;
        },
        [shouldParse, intlApi.formatMessage],
    );

    const getOptionValueParsed = useCallback(
        optionValue =>
            shouldParse && optionValue !== '' && optionValue !== 'default'
                ? intlApi.formatMessage({ id: optionValue })
                : optionValue,
        [shouldParse, intlApi],
    );

    const optionsNormalized = useMemo(() => {
        return options.map(option => ({
            ...option,
            label: getOptionTextParsed(option.text),
            value: getOptionValueParsed(option.value),
        }));
    }, [options, getOptionTextParsed, getOptionValueParsed]);

    const optionSelected = useMemo(() => {
        const [first] = optionsNormalized;

        const option = optionsNormalized.find(optionNormalized => optionNormalized.value === value);

        if (value === null || !option) {
            return first;
        }

        return option;
    }, [optionsNormalized, value]);

    const handleValueChange = useCallback(
        optionSelected => {
            let valueForSaving;
            const option = optionsNormalized.find(option => option.optionId === optionSelected.optionId);
            const text = option && option.value !== 'default' ? option.text : null;

            if (option) {
                const { value } = option;

                switch (true) {
                    case value === 'default':
                        valueForSaving = normalizeDefaultToNull(value);
                        break;
                    case handleAsNumbers:
                        valueForSaving = +value;
                        break;
                    case returnAsBoolean:
                        valueForSaving = normalizeStringToBooleanValue(value);
                        break;
                    default:
                        valueForSaving = value;
                }
            }

            inputActions.handleFieldChange(valueForSaving, text);
        },
        [optionsNormalized, inputActions.handleFieldChange],
    );

    const inputId = `rs_${id}`;

    const modalSelectProps = useMemo(() => {
        if (!isInModal) {
            return {};
        }

        return {
            menuPosition: 'fixed',
            menuPortalTarget: document.body,
        };
    }, [isInModal]);
    return (
        <div className={classNames(`grid-row gi-component gi-${type}`, dropdownContainerClassName)}>
            <label
                htmlFor={inputId}
                data-testid={`${id}_label`}
                className={classNames(
                    `tablet:grid-col-4 m-0 gi-${type}__label iconed-${!!labelIcon.icon} required-${isInputRequired}`,
                    labelClass,
                )}
            >
                {text}
                {labelIcon.icon ? labelIcon.icon : null}
            </label>

            <Select
                {...modalSelectProps}
                name={name}
                openMenuOnFocus
                inputId={inputId}
                aria-label={ariaLabel}
                isSearchable={false}
                isDisabled={disabled}
                value={optionSelected}
                classNamePrefix="gi-rs"
                inputClass={inputClass}
                options={optionsNormalized}
                onChange={handleValueChange}
                isOptionDisabled={option => option.isGroupHeader === true || option.disabled === true}
                className="tablet:grid-col-6 gi-rs"
                data-testid={`${id}_select`}
                components={{
                    IndicatorSeparator: () => null,
                    Option: ReactSelectOption,
                }}
            />
        </div>
    );
};

export default ReactSelect;
